import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { DialogService } from '@core/services/dialog.service';
import { NotificationService } from '@core/services/notification.service';
import { UserMaintenanceSecurityWrapperService } from '@core/services/service-wrappers/user-maintenance-security-wrapper.service';
import { DialogContent } from 'components';
import { observeOn, take } from 'rxjs/operators';
import { AddEditUserDialogComponent } from './add-edit-user-dialog/add-edit-user-dialog.component';
import { AssignDatasetsDialogComponent } from './assign-datasets-dialog/assign-datasets-dialog.component';
import { AssignRolesDialogComponent } from './assign-roles-dialog/assign-roles-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ImplementationWrapperService } from '@core/services/service-wrappers/implementation-wrapper.service';
import { Store } from '@ngrx/store';
import { ImplementationState } from '../../../implementation/state/implementation.state';
import { asyncScheduler, Observable, Subscription } from 'rxjs';
import { ImplementationDetailViewModel, ImplementationViewModel, UserDetailsViewModel } from 'data';
import { selectImplementation } from '../../../implementation/state/implementation.selectors';
import * as ImplementationActions from '../../../implementation/state/implementation.actions';
import { SessionStorageService } from '@core/services/session-storage.service';

export interface DataSets {
  name: string;
  email: string;
  roles: string[];
  dataSets: string[];
}

@Component({
  standalone: false,
  selector: 'app-users',
  templateUrl: './users.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./users.component.scss']
})
export class UsersComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['action', 'name', 'email', 'roles', 'dataSets'];
  usersList = [];
  usersDataSource = new MatTableDataSource<any>([]);
  // The initial request starts in ngOnInit. Begin in the loading state so the
  // first change-detection pass does not observe false before the request sets
  // this value to true.
  loading = true;
  error = false;
  userListError = '';
  public length: number = 0;
  currentPage: number = 1;
  userInfo: UserDetailsViewModel;
  viewOnly = false;
  overflowMenuOptions = [
    {
      id: 'edit',
      label: 'Edit User'
    },
    {
      id: 'roles',
      label: 'Assign Roles'
    },
    {
      id: 'datasets',
      label: 'Assign Datasets'
    },
    {
      id: 'delete',
      label: 'Delete User'
    }
  ];
  overflowViewOnly = [
    {
      id: 'view',
      label: 'View User'
    }
  ];

  constructor(
    private dialogService: DialogService,
    public dialog: MatDialog,
    private userMaintenanceSecurityService: UserMaintenanceSecurityWrapperService,
    private notificationService: NotificationService,
    private activatedRoute: ActivatedRoute,
    private implementationService: ImplementationWrapperService,
    private router: Router,
    private sessionStorageService: SessionStorageService,
    private store: Store<ImplementationState>
  ) {}

  ngOnInit(): void {
    this.userInfo = this.sessionStorageService.getItem('user-info');
    var permissionValue = this.userInfo?.userPermissions?.find((x) => x.permissionType == 'Users');
    if (permissionValue?.accessType !== 'Update') {
      this.viewOnly = true;
      this.overflowMenuOptions = this.overflowViewOnly;
    }
    this.getUserList();
    this.implementationSubscribe();
  }

  getUserList(page = 1, pageSize = 25, showLoading = true): void {
    if (showLoading) {
      this.loading = true;
    }
    this.userMaintenanceSecurityService
      .apiV1UserPost({
        page,
        pageSize
      })
      .pipe(take(1), observeOn(asyncScheduler))
      .subscribe(
        (response) => {
          this.applyUserListResponse(response);
        },
        (error) => {
          this.applyUserListError(error);
        }
      );
  }

  overflowMenuClicked(value, data) {
    if (value === 'delete') {
      this.openDeleteUserModal(data.id);
    } else if (value === 'roles') {
      this.assignRoles(data);
    } else if (value === 'datasets') {
      this.openAssignDatasetsModal(data);
    } else if (value === 'edit') {
      this.addEditUser(data);
    } else if (value === 'view') {
      this.addEditUser(data, true);
    }
  }

  addEditUser(data = null, viewOnly = false) {
    this.openAddEditUserModal(data, viewOnly);
  }

  assignRoles(data) {
    this.openAssignRolesModal(data);
  }

  openAddEditUserModal(data, viewOnly = false) {
    const dialog = this.dialog.open(AddEditUserDialogComponent, {
      disableClose: true,
      autoFocus: false,
      data: { data: data, viewOnly: viewOnly }
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.refreshUserList();
      }
    });
  }

  openAssignRolesModal(data) {
    const dialog = this.dialog.open(AssignRolesDialogComponent, {
      disableClose: true,
      autoFocus: false,
      data: { data }
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.refreshUserList();
      }
    });
  }

  openAssignDatasetsModal(data) {
    const dialog = this.dialog.open(AssignDatasetsDialogComponent, {
      disableClose: true,
      autoFocus: false,
      data: { data }
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.refreshUserList();
      }
    });
  }

  openDeleteUserModal(userId: string) {
    const modalContent: DialogContent = {
      header: '',
      body: 'Are you sure you want to delete this user?',
      subBody: 'You are about to delete this user, would you like to continue?',
      cancelButtonText: 'Back',
      OKButtonText: 'Confirm'
    };
    this.dialogService.showConfirmDialog(modalContent).subscribe((result) => {
      if (result) {
        this.userMaintenanceSecurityService
          .apiV1UserDeleteUserDelete(userId)
          .pipe(
            take(1),
            observeOn(asyncScheduler)
          )
          .subscribe((deleteResult) => {
            this.notificationService.success('User Deleted');
            this.refreshUserList();
          });
      }
    });
  }

  private mapUser(user: any): any {
    const firstName = user?.firstName ?? '';
    const lastName = user?.lastName ?? '';

    return {
      ...user,
      fullName: user?.fullName ?? `${firstName} ${lastName}`.trim(),
      userRoles: user?.userRoles ?? user?.roles?.map((role) => ({ ...role, name: role.name ?? role.roleName })) ?? [],
      userDatasets: user?.userDatasets ?? user?.datasets ?? []
    };
  }

  private applyUserListResponse(response: any): void {
    const users = response?.data ?? [];
    const mappedUsers = users.map((user) => this.mapUser(user));
    const visibleUsers = mappedUsers.filter((d) => d.fullName !== 'sysadmin');
    const sysadmins = mappedUsers.filter((d) => d.fullName === 'sysadmin');

    this.loading = false;
    this.error = false;
    this.usersList = visibleUsers;
    this.usersDataSource.data = visibleUsers;
    this.length = (response?.metaData?.totalItemCount ?? visibleUsers.length) - sysadmins.length;
  }

  private applyUserListError(error: any): void {
    this.loading = false;
    this.error = true;

    if (error.status === 403) {
      this.userListError = 'Current user is not authorized to view Users.';
    } else {
      this.userListError = 'Error while accessing users.  Please contact support.';
    }
  }

  handlePageEvent(event: PageEvent) {
    this.currentPage = event.pageIndex + 1;
    this.getUserList(this.currentPage, event.pageSize);
  }

  private refreshUserList(): void {
    setTimeout(() => this.getUserList(this.currentPage, 25, false));
  }

  public isImplementation: boolean = false;
  public gonext: boolean = false;
  public itemCount: number = 0;
  private activatedRouteSub: Subscription;
  private implementation$: Observable<ImplementationViewModel> = this.store.select(selectImplementation);
  private implementationSub: Subscription;
  private implementation: ImplementationViewModel = null;
  private implementationDetail: ImplementationDetailViewModel = null;
  private nextImplementationDetail: ImplementationDetailViewModel = null;
  private STEP_CODE = 'users';

  implementationSubscribe() {
    this.activatedRouteSub = this.activatedRoute.data.subscribe((data) => {
      this.isImplementation = data.implementation;

      if (this.isImplementation) {
        this.implementationSub = this.implementation$.subscribe((i) => {
          this.implementation = i;
          this.implementationDetail = this.implementation?.details?.find(
            (d) => d.implementationStepCode == this.STEP_CODE
          );
          this.nextImplementationDetail = this.implementationDetail
            ? this.implementation?.details?.find((d) => d.order == this.implementationDetail.order + 1)
            : null;
        });
      }
    });
    this.store.dispatch(ImplementationActions.updateImplementationStep({ stepCode: this.STEP_CODE }));
  }

  implementationUnsubscribe() {
    if (this.isImplementation) {
      this.activatedRouteSub.unsubscribe();
      this.implementationSub.unsubscribe();
    }
  }

  next(): void {
    if (!this.implementationDetail || !this.nextImplementationDetail) {
      return;
    }

    if (this.implementationDetail.status == 'InProgress') {
      this.gonext = true;
      this.implementationService.apiV1ImplementationCompleteStepCodePost(this.STEP_CODE).subscribe(
        () => {
          this.gonext = false;
          this.router.navigateByUrl('/implementation/' + this.nextImplementationDetail.implementationStepCode);
        },
        () => (this.gonext = false)
      );
    } else {
      this.router.navigateByUrl('/implementation/' + this.nextImplementationDetail.implementationStepCode);
    }
  }

  ngOnDestroy(): void {
    this.implementationUnsubscribe();
  }

  trackById(index: number, item: any) {
    return item.id;
  }
}
